'use client';

import * as Sentry from '@sentry/nextjs';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useEffect } from 'react';
import { GTM_ID, pageview } from '@/utils/gtm';

function getUtmParams(searchParams: URLSearchParams) {
  const campaign: Record<string, string> = {};
  if (searchParams.get('utm_content')) campaign.content = searchParams.get('utm_content')!;
  if (searchParams.get('utm_campaign')) campaign.name = searchParams.get('utm_campaign')!;
  if (searchParams.get('utm_medium')) campaign.medium = searchParams.get('utm_medium')!;
  if (searchParams.get('utm_source')) campaign.source = searchParams.get('utm_source')!;
  if (searchParams.get('utm_term')) campaign.keyword = searchParams.get('utm_term')!;
  return campaign;
}

function getUserAgentData() {
  if (typeof navigator === 'undefined' || !('userAgentData' in navigator)) {
    return null;
  }

  // Type assertion since TypeScript might not recognize userAgentData
  const uaData = (navigator as any).userAgentData;
  if (!uaData) {
    return null;
  }
  return {
    userAgentData: {
      brands: uaData.brands,
      mobile: uaData.mobile,
      platform: uaData.platform,
    },
  };
}

function getSessionId() {
  const COOKIE_NAME = 'session_id';
  const EXPIRY_MINUTES = 30;

  // Helper to get cookie expiration time
  const getExpiryTime = () => {
    const date = new Date();
    date.setTime(date.getTime() + EXPIRY_MINUTES * 60 * 1000);
    return date.toUTCString();
  };

  // Try to get existing session ID
  const existingSessionId = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${COOKIE_NAME}=`))
    ?.split('=')[1];

  if (existingSessionId) {
    // Update existing session cookie with new expiry
    document.cookie = `${COOKIE_NAME}=${existingSessionId}; expires=${getExpiryTime()}; path=/`;
    return Number(existingSessionId);
  }

  // Create new session ID if none exists
  const newSessionId = Date.now().toString();
  document.cookie = `${COOKIE_NAME}=${newSessionId}; expires=${getExpiryTime()}; path=/`;
  console.log('newSessionId', newSessionId);
  return Number(newSessionId);
}

function Analytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const track = async () => {
      try {
        if (pathname) {
          pageview(pathname);
        }
      } catch (error) {
        console.error('Failed to track page view:', error);
      }

      const anonymousId = getAnonymousId();
      const context: any = {
        ...(typeof navigator !== 'undefined' && {
          userAgent: navigator.userAgent,
          locale: navigator.language,
          ...getUserAgentData(),
        }),
        campaign: getUtmParams(searchParams),
        ...getPageContext(),
      };

      const properties = {
        path: pathname,
        url: pathname + searchParams.toString(),
        search: searchParams.toString(),
        host: window.location.host,
        referrer: document.referrer,
      };

      try {
        await fetch('/api/analytics/page', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            anonymousId,
            pageName: pathname,
            data: properties,
            context,
            sessionId: getSessionId(),
          }),
        });
      } catch (error) {
        console.error('Failed to send analytics:', error);
      }
    };

    track();
  }, [pathname, searchParams]);

  return (
    <>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer', '${GTM_ID}');
  `,
        }}
      />
    </>
  );
}

function generateFallbackUUID() {
  // Simple UUID v4 implementation for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getAnonymousId() {
  try {
    let anonymousId = localStorage.getItem('anonymous_id');

    if (!anonymousId) {
      // Try to use crypto.randomUUID with fallback for older browsers
      try {
        anonymousId = crypto.randomUUID();
      } catch (err) {
        anonymousId = generateFallbackUUID();
        console.warn('Using fallback UUID generator as crypto.randomUUID is not available');
      }
      localStorage.setItem('anonymous_id', anonymousId);
    }
    return anonymousId;
  } catch (error) {
    try {
      return crypto.randomUUID();
    } catch (err) {
      return generateFallbackUUID();
    }
  }
}

function getPageContext() {
  if (typeof window === 'undefined') return {};

  return {
    page: {
      path: window.location.pathname,
      url: window.location.href,
      search: window.location.search,
      host: window.location.host,
      referrer: document.referrer,
    },
  };
}

function analyticsTrack(eventName: string, data: any = {}) {
  const anonymousId = getAnonymousId();
  const context: any = {};

  if (typeof navigator !== 'undefined') {
    context.userAgent = navigator.userAgent;
    context.locale = navigator.language;
    Object.assign(context, getUserAgentData());
  }
  if (typeof window !== 'undefined' && window.location) {
    context.campaign = getUtmParams(new URLSearchParams(window.location.search));
    Object.assign(context, getPageContext());
  }

  fetch('/api/analytics/track', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      eventName,
      data: data,
      context,
      anonymousId,
      sessionId: getSessionId(),
    }),
  }).catch(err => {
    console.warn('Analytics tracking request failed:', err);
  });

  try {
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: 'Analytics event: ' + eventName,
      level: 'info',
      data,
    });
  } catch (error) {
    console.warn('Failed to add Sentry breadcrumb:', error);
  }
}

function analyticsIdentify(userId: string, userTraits: any = {}) {
  const anonymousId = getAnonymousId();
  const context: any = {};

  if (typeof navigator !== 'undefined') {
    context.userAgent = navigator.userAgent;
    context.locale = navigator.language;
    Object.assign(context, getUserAgentData());
  }
  if (typeof window !== 'undefined' && window.location) {
    context.campaign = getUtmParams(new URLSearchParams(window.location.search));
    Object.assign(context, getPageContext());
  }

  fetch('/api/analytics/identify', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
      traits: userTraits,
      context,
      anonymousId,
      sessionId: getSessionId(),
    }),
  }).catch(err => {
    console.warn('Analytics identify request failed:', err);
  });
}

export { Analytics, getAnonymousId, analyticsTrack, analyticsIdentify };
